import { Suspense, lazy } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { PageVisibilityGuard } from "@/components/PageVisibilityGuard";
import { usePrefetchCriticalData } from "@/hooks/usePrefetchCriticalData";
import { LazyToasters } from "@/components/ui/LazyToasters";
import DebugOverlay from "@/components/DebugOverlay";

// Lazy load ProtectedRoute to defer Supabase Auth module loading
const ProtectedRoute = lazy(() => import("@/components/admin/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));

// Critical pages loaded immediately (homepage and common pages)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Public pages - lazy loaded
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const About = lazy(() => import("./pages/About"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Process = lazy(() => import("./pages/Process"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Faqs = lazy(() => import("./pages/Faqs"));
const Areas = lazy(() => import("./pages/Areas"));
const AreaDetail = lazy(() => import("./pages/AreaDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Quote = lazy(() => import("./pages/Quote"));
const SocialMedia = lazy(() => import("./pages/SocialMedia"));
const TestimonialsPage = lazy(() => import("./pages/Testimonials"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Terms = lazy(() => import("./pages/Terms"));

// Admin pages - lazy loaded
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminServices = lazy(() => import("./pages/admin/Services"));
const ServiceForm = lazy(() => import("./pages/admin/ServiceForm"));
const AdminAreas = lazy(() => import("./pages/admin/Areas"));
const AreaForm = lazy(() => import("./pages/admin/AreaForm"));
const AdminFaqs = lazy(() => import("./pages/admin/Faqs"));
const FaqForm = lazy(() => import("./pages/admin/FaqForm"));
const AdminProjects = lazy(() => import("./pages/admin/Projects"));
const ProjectForm = lazy(() => import("./pages/admin/ProjectForm"));
const BlogPosts = lazy(() => import("./pages/admin/BlogPosts"));
const BlogPostForm = lazy(() => import("./pages/admin/BlogPostForm"));
const BlogCategories = lazy(() => import("./pages/admin/BlogCategories"));
const AdminTestimonials = lazy(() => import("./pages/admin/Testimonials"));
const TestimonialForm = lazy(() => import("./pages/admin/TestimonialForm"));
const TestimonialsPageEditor = lazy(() => import("./pages/admin/TestimonialsPageEditor"));
const AreasPageEditor = lazy(() => import("./pages/admin/AreasPageEditor"));
const ProjectsPageEditor = lazy(() => import("./pages/admin/ProjectsPageEditor"));
const BlogPageEditor = lazy(() => import("./pages/admin/BlogPageEditor"));
const FaqsPageEditor = lazy(() => import("./pages/admin/FaqsPageEditor"));
const QuotePageEditor = lazy(() => import("./pages/admin/QuotePageEditor"));
const SocialPageEditor = lazy(() => import("./pages/admin/SocialPageEditor"));
const Submissions = lazy(() => import("./pages/admin/Submissions"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const ResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const MediaLibrary = lazy(() => import("./pages/admin/MediaLibrary"));
const AdminPages = lazy(() => import("./pages/admin/Pages"));
const HomepageEditor = lazy(() => import("./pages/admin/HomepageEditor"));
const AboutEditor = lazy(() => import("./pages/admin/AboutEditor"));
const ContactEditor = lazy(() => import("./pages/admin/ContactEditor"));
const ProcessEditor = lazy(() => import("./pages/admin/ProcessEditor"));
const ServicesPageEditor = lazy(() => import("./pages/admin/ServicesPageEditor"));
const PricingPageEditor = lazy(() => import("./pages/admin/PricingPageEditor"));
const Navigation = lazy(() => import("./pages/admin/Navigation"));
const Chatbot = lazy(() => import("./pages/admin/Chatbot"));
const Authors = lazy(() => import("./pages/admin/Authors"));
const NotificationsEditor = lazy(() => import("./pages/admin/NotificationsEditor"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

// Inner app component that uses prefetch hook
const AppContent = () => {
  // Prefetch critical data early
  usePrefetchCriticalData();
  
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Index />} />
          <Route path="/services" element={<PageVisibilityGuard settingKey="services_page_visible"><Services /></PageVisibilityGuard>} />
          <Route path="/services/:slug" element={<PageVisibilityGuard settingKey="services_page_visible"><ServiceDetail /></PageVisibilityGuard>} />
          <Route path="/about" element={<PageVisibilityGuard settingKey="about_page_visible"><About /></PageVisibilityGuard>} />
          <Route path="/pricing" element={<PageVisibilityGuard settingKey="pricing_page_visible"><Pricing /></PageVisibilityGuard>} />
          <Route path="/process" element={<PageVisibilityGuard settingKey="process_page_visible"><Process /></PageVisibilityGuard>} />
          <Route path="/projects" element={<PageVisibilityGuard settingKey="projects_page_visible"><Projects /></PageVisibilityGuard>} />
          <Route path="/projects/:slug" element={<PageVisibilityGuard settingKey="projects_page_visible"><ProjectDetail /></PageVisibilityGuard>} />
          {/* Redirect old /results URLs to /projects for SEO */}
          <Route path="/results" element={<Navigate to="/projects" replace />} />
          <Route path="/results/:slug" element={<Navigate to="/projects/:slug" replace />} />
          <Route path="/faqs" element={<PageVisibilityGuard settingKey="faqs_page_visible"><Faqs /></PageVisibilityGuard>} />
          <Route path="/areas" element={<PageVisibilityGuard settingKey="areas_page_visible"><Areas /></PageVisibilityGuard>} />
          <Route path="/areas/:slug" element={<PageVisibilityGuard settingKey="areas_page_visible"><AreaDetail /></PageVisibilityGuard>} />
          <Route path="/blog" element={<PageVisibilityGuard settingKey="blog_page_visible"><Blog /></PageVisibilityGuard>} />
          <Route path="/blog/:slug" element={<PageVisibilityGuard settingKey="blog_page_visible"><BlogPost /></PageVisibilityGuard>} />
          <Route path="/contact" element={<PageVisibilityGuard settingKey="contact_page_visible"><Contact /></PageVisibilityGuard>} />
          <Route path="/quote" element={<PageVisibilityGuard settingKey="quote_page_visible"><Quote /></PageVisibilityGuard>} />
          <Route path="/follow-us" element={<PageVisibilityGuard settingKey="social_page_visible"><SocialMedia /></PageVisibilityGuard>} />
          <Route path="/testimonials" element={<PageVisibilityGuard settingKey="testimonials_page_visible"><TestimonialsPage /></PageVisibilityGuard>} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms" element={<Terms />} />
          
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/admin/submissions" element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
          <Route path="/admin/pages" element={<ProtectedRoute><AdminPages /></ProtectedRoute>} />
          <Route path="/admin/pages/homepage" element={<ProtectedRoute><HomepageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/about" element={<ProtectedRoute><AboutEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/contact" element={<ProtectedRoute><ContactEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/process" element={<ProtectedRoute><ProcessEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/services" element={<ProtectedRoute><ServicesPageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/pricing" element={<ProtectedRoute><PricingPageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/testimonials" element={<ProtectedRoute><TestimonialsPageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/areas" element={<ProtectedRoute><AreasPageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/projects" element={<ProtectedRoute><ProjectsPageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/blog" element={<ProtectedRoute><BlogPageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/faqs" element={<ProtectedRoute><FaqsPageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/quote" element={<ProtectedRoute><QuotePageEditor /></ProtectedRoute>} />
          <Route path="/admin/pages/social" element={<ProtectedRoute><SocialPageEditor /></ProtectedRoute>} />
          <Route path="/admin/navigation" element={<ProtectedRoute><Navigation /></ProtectedRoute>} />
          <Route path="/admin/media" element={<ProtectedRoute><MediaLibrary /></ProtectedRoute>} />
          <Route path="/admin/services" element={<ProtectedRoute><AdminServices /></ProtectedRoute>} />
          <Route path="/admin/services/new" element={<ProtectedRoute><ServiceForm /></ProtectedRoute>} />
          <Route path="/admin/services/:id" element={<ProtectedRoute><ServiceForm /></ProtectedRoute>} />
          <Route path="/admin/areas" element={<ProtectedRoute><AdminAreas /></ProtectedRoute>} />
          <Route path="/admin/areas/new" element={<ProtectedRoute><AreaForm /></ProtectedRoute>} />
          <Route path="/admin/areas/:id" element={<ProtectedRoute><AreaForm /></ProtectedRoute>} />
          <Route path="/admin/faqs" element={<ProtectedRoute><AdminFaqs /></ProtectedRoute>} />
          <Route path="/admin/faqs/new" element={<ProtectedRoute><FaqForm /></ProtectedRoute>} />
          <Route path="/admin/faqs/:id" element={<ProtectedRoute><FaqForm /></ProtectedRoute>} />
          <Route path="/admin/projects" element={<ProtectedRoute><AdminProjects /></ProtectedRoute>} />
          <Route path="/admin/projects/new" element={<ProtectedRoute><ProjectForm /></ProtectedRoute>} />
          <Route path="/admin/projects/:id" element={<ProtectedRoute><ProjectForm /></ProtectedRoute>} />
          <Route path="/admin/blog" element={<ProtectedRoute><BlogPosts /></ProtectedRoute>} />
          <Route path="/admin/blog/categories" element={<ProtectedRoute><BlogCategories /></ProtectedRoute>} />
          <Route path="/admin/blog/new" element={<ProtectedRoute><BlogPostForm /></ProtectedRoute>} />
          <Route path="/admin/blog/:id" element={<ProtectedRoute><BlogPostForm /></ProtectedRoute>} />
          <Route path="/admin/testimonials" element={<ProtectedRoute><AdminTestimonials /></ProtectedRoute>} />
          <Route path="/admin/testimonials/new" element={<ProtectedRoute><TestimonialForm /></ProtectedRoute>} />
          <Route path="/admin/testimonials/:id" element={<ProtectedRoute><TestimonialForm /></ProtectedRoute>} />
          <Route path="/admin/chatbot" element={<ProtectedRoute><Chatbot /></ProtectedRoute>} />
          <Route path="/admin/authors" element={<ProtectedRoute><Authors /></ProtectedRoute>} />
          <Route path="/admin/notifications" element={<ProtectedRoute><NotificationsEditor /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
// App component
const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        <TooltipProvider>
          <LazyToasters />
          <DebugOverlay />
          <AppContent />
        </TooltipProvider>
      </SettingsProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
